import React, { useState, useEffect, useCallback } from "react";
import { Box, Card, CardContent, Typography, Divider, Rating } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { decryptId } from "../helper/idCodec.js";
import { Row, Col } from "react-bootstrap";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext.js"; // ✅ Import spinner
import "./Trucks.css";

function TruckDetails() {
    const { id } = useParams();
    const truckId = id ? decryptId(id) : "";
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [truck, setTruck] = useState(null);
    const navigate = useNavigate();

    const fetchTruckData = useCallback(async () => {
        if (!truckId) {
            setError("Truck ID not found.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        const startTime = Date.now(); // Track start time for minimum loading

        try {
            const url = API_ENDPOINTS.getTruckDetailsById.replace(":truck_id", truckId);
            const response = await getData(url);

            if (response.statusCode !== 200) {
                throw new Error(response.message || "Failed to retrieve truck data.");
            }

            const truckData = response.result;

            const mappedTruckData = {
                vehicleId: truckData.truck_id,
                truckName: truckData.truck_name,
                truckModel: truckData.truck_model,
                truckNumberPlate: truckData.truck_no,
                truckImage: truckData.truck_pic,
                rating: truckData.average_rating || 0,
                ownerId: truckData.owner_id,
                ownerName: truckData.owner_name,
                truckCapacityInKgs: truckData.capacity_inkgs,
                capacityUnits: truckData.capacity_inspace,
                ownedByCompany: truckData.owned_by_transport_company,
                rcFile: truckData.rc_file,
                createdAt: truckData.createdAt,
                updatedAt: truckData.updatedAt,
            };

            setTruck(mappedTruckData);
        } catch (err) {
            setError(err.message || "Failed to fetch truck profile data.");
        } finally {
            // Ensure minimum 3 seconds loading
            const elapsed = Date.now() - startTime;
            const remaining = 3000 - elapsed;
            setTimeout(() => setLoading(false), remaining > 0 ? remaining : 0);
        }
    }, [truckId]);

    useEffect(() => {
        fetchTruckData();
    }, [fetchTruckData]);

    if (!truck && !error) {
        return <LoadingSpinner isLoading={loading} />;
    }

    if (error) {
        return (
            <>
                <LoadingSpinner isLoading={loading} />
                <Typography variant="h6" color="error" textAlign="center">
                    {error}
                </Typography>
            </>
        );
    }

    return (
        <>
            <LoadingSpinner isLoading={loading} />
            <Box className="driver-details-container" p={2}>
                <Box display="flex" alignItems="center" justifyContent="flex-start" gap={2} mb={2}>
                    <img
                        src="/smart-vectors/Back.svg"
                        alt="back"
                        style={{ width: 32, height: 32, cursor: "pointer" }}
                        onClick={() => navigate(-1)}
                    />
                    <Box>
                        <Typography variant="h4" fontWeight={600} gutterBottom className="title">
                            {truck.truckName}
                        </Typography>
                        <Typography variant="subtitle1" color="text.secondary" mb={1} className="sub-title">
                            {truck.truckModel ? truck.truckModel : "Unknown"}
                        </Typography>
                    </Box>
                </Box>

                <Row>
                    <Col md={6} className="mb-3 mb-md-0">
                        <Card elevation={3} style={{ height: "100%" }}>
                            <CardContent style={{ padding: "0" }}>
                                <Box
                                    sx={{
                                        width: "100%",
                                        paddingTop: "66.66%",
                                        position: "relative",
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                    }}
                                >
                                    <img
                                        src={truck.truckImage && truck.truckImage.trim() !== "" ? truck.truckImage : "/images/Default-Truck.png"}
                                        alt={`${truck.truckName} Truck`}
                                        style={{
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Col>

                    <Col md={6}>
                        <Card elevation={3}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        General Information
                                    </Typography>
                                    <Box display="flex" alignItems="center">
                                        <Rating value={truck.rating} precision={0.5} readOnly />
                                        <Typography variant="body1" fontWeight={600}>
                                            ({truck.rating}/5)
                                        </Typography>
                                    </Box>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Typography mb={0.5} variant="body2" color="text.secondary" fontWeight={500}>
                                    Owner : {truck.ownerName}
                                </Typography>
                                <Typography mb={0.5} variant="body2" color="text.secondary" fontWeight={500}>
                                    Capacity (Kgs) : {truck.truckCapacityInKgs} Kgs
                                </Typography>
                                <Typography mb={0.5} variant="body2" color="text.secondary" fontWeight={500}>
                                    Capacity (Space) : {truck.capacityUnits} Units
                                </Typography>
                                <Typography mb={0.5} variant="body2" color="text.secondary" fontWeight={500}>
                                    Owned by Transport Company :
                                    {truck.ownedByCompany === true
                                        ? "Yes"
                                        : truck.ownedByCompany === false
                                            ? "No"
                                            : "N/A"}
                                </Typography>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Associated Files
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    className="title2"
                                    mb={0.5}
                                >
                                    RC File :{" "}
                                    {truck.rcFile ? (
                                        <a
                                            href={truck.rcFile}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: "#1976d2",
                                                fontWeight: 600,
                                                textDecoration: "none",
                                                cursor: "pointer",
                                            }}
                                        >
                                            View
                                        </a>
                                    ) : (
                                        <span style={{ color: "gray" }}>Not Available</span>
                                    )}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    fontWeight={500}
                                    className="title2"
                                    mb={0.5}
                                >
                                    Pollution Certificate :{" "}
                                    {truck.rcFile ? (
                                        <a
                                            href={truck.rcFile}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: "#1976d2",
                                                fontWeight: 600,
                                                textDecoration: "none",
                                                cursor: "pointer",
                                            }}
                                        >
                                            View
                                        </a>
                                    ) : (
                                        <span style={{ color: "gray" }}>Not Available</span>
                                    )}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Col>
                </Row>
            </Box>
        </>
    );
}

export default TruckDetails;
